import * as Sentry from '@sentry/browser';
import React from 'react';
import ReactDOM from 'react-dom';
import {ApolloProvider} from 'react-apollo';
import {ConnectedRouter} from 'connected-react-router';
import {Provider} from 'react-redux';

import './polyfills';
import {
  auth,
  store,
  apolloClient,
  apolloClient2,
  history,
  debugCollector,
} from './setup';
import * as serviceWorker from './serviceWorker';
import config, {envIsDev, envIsIntegration} from './config';
import Routes from './routes';
import {Apollo2Context} from './util/apollo2-hooks';
import {getFullStoryUrl} from './util/integrations';
import './index.css';
import 'semantic/dist/semantic.min.css';
import {Auth0Provider} from './react-auth0-spa';

Sentry.init({
  dsn: config.SENTRY_DSN,
  environment: config.SENTRY_ENVIRONMENT,
  release: config.GIT_TAG,
  normalizeDepth: Infinity,
  beforeSend(event, hint) {
    const error = hint && hint.originalException;
    event.extra = event.extra || {};
    event.extra.fullstory =
      getFullStoryUrl() || 'current session URL API not ready';

    if (window.FS && window.FS.event) {
      window.FS.event('Application Error', {
        name: typeof error === 'string' ? error : error?.name,
        message: typeof error === 'string' ? error : error?.message,
        fileName: typeof error !== 'string' && error?.message,
        stack: typeof error !== 'string' && error?.stack,
        sentryEventId: hint?.event_id,
      });
    }

    return event;
  },
});

if (config.ENVIRONMENT_IS_PRIVATE) {
  // this obfuscates the original log call locations, so only use it
  // in local, when Sentry et all aren't available
  debugCollector.instrument();
}

const shouldRedirectToHTTPS = () =>
  (envIsDev || envIsIntegration) &&
  window.location.hostname !== 'localhost' &&
  window.location.protocol !== 'https:';

const render = async (Component: React.FC<{auth: typeof auth}>) => {
  if (shouldRedirectToHTTPS()) {
    window.location.protocol = 'https:';
    return;
  }
  // Attempt to login automatically for W&B local
  if ('autoLogin' in auth && auth.autoLogin != null) {
    await auth.autoLogin();
  }
  const AuthProvider =
    'auth0Client' in auth ? Auth0Provider : (props: any) => props.children;
  ReactDOM.render(
    <Provider store={store}>
      <ApolloProvider client={apolloClient}>
        <Apollo2Context.Provider value={apolloClient2}>
          <AuthProvider>
            <ConnectedRouter history={history}>
              <Component auth={auth} />
            </ConnectedRouter>
          </AuthProvider>
        </Apollo2Context.Provider>
      </ApolloProvider>
    </Provider>,
    document.getElementById('root')
  );
};
// Note: Routes is App from App.js
render(Routes);

if (module.hot) {
  module.hot.accept('./routes', () => {
    const NextApp = require('./routes').default;
    render(NextApp);
  });
}

// We've disabled service workers because of caching issues described here:
// https://jeffy.info/2018/10/10/sw-in-c-r-a.html
if (config.ENABLE_SERVICE_WORKER) {
  serviceWorker.register();
} else {
  serviceWorker.unregister();
}
